import { Col, Container, Modal, ModalBody, ModalHeader, Row } from "reactstrap";
import { useEffect, useRef, useState } from "react";
import { useDispatch } from "react-redux";
import { AxiosResponse } from "axios";
import { ToolCard } from "../tool-card/tool-card";
import './tool-modal.css';
import { Button, Form, Input, InputNumber, Upload, UploadFile } from "antd";
import { UploadOutlined } from "@ant-design/icons";
import { UploadChangeParam } from "antd/lib/upload";
import * as THREE from 'three';
import { Canvas } from "@react-three/fiber";
import { OrbitControls } from "@react-three/drei";
import { GetToolsSuccessResponse, Tool } from "../../types/api/tool/get-tools-success-response";
import { showLoadingAlertMessage } from "../../../../utils/show-loading-alert-message/show-loading-alert-message";
import { networkCall } from "../../../../utils/network-call/network-call";
import { AlertMessageState, clearAlertMessage, showAlertMessage } from "../../../../redux/reducer/alert-message.reducer";
import { showDefaultErrorMessages } from "../../../../utils/show-default-error-message/show-default-error-messages";
import { AlertMessages } from "../../../../enums/alert-messages.enum";
import { CreateToolSuccessResponse } from "../../types/api/tool/create-tool-success-response ";
import { DefaultErrorResponse } from "../../../../types/api/error/default-error-response/default-error-respose.type";
import { ValidationErrorResponseData } from "../../../../types/api/error/validation-error-response-data/validation-error-response-data.type.";
import { tranformAPIValidationErrorToAntDValidationMessage } from "../../../../utils/validation/transform-api-validation-errors-to-antd-validation-messsages";
import { getImageUrl } from "../../../../utils/image-helper/get-image-url";
import { CMSButton } from "../../../../components/cms/button/cms-button.";

type ToolModalProps = {
    isListModalOpen: boolean;
    setIsListModalOpen: (isOpen: boolean) => void;
};

const normFile = (event: any) => {
    if (Array.isArray(event)) return event;
    return event?.fileList;
};

const loadModelFromFile = (file: File): THREE.Group<THREE.Object3DEventMap> | THREE.Object3D | undefined => {
    const extension = file.name.split('.').pop()?.toLowerCase();
    let loadedModel: THREE.Group<THREE.Object3DEventMap> | THREE.Object3D | undefined = undefined;
    
    switch (extension) {
        case '3dm': {
            const fileReader = new FileReader();

            fileReader.addEventListener('load', async (event) => {
                const { Rhino3dmLoader } = await import('three/addons/loaders/3DMLoader.js');

                const loader = new Rhino3dmLoader();
                loader.setLibraryPath(`${process.env.REACT_APP_ORIGIN}/libs/rhino3dm/`);

                loader.parse(event.target?.result as ArrayBufferLike , (object) => { loadedModel = object; });
            }, false);
            fileReader.readAsArrayBuffer(file);

            break;
        }
        case 'glb':
        case 'gltf': {
            const fileReader = new FileReader();

            fileReader.addEventListener('load', async (event) => {
                const manager = new THREE.LoadingManager();

                const { GLTFLoader } = await import('three/addons/loaders/GLTFLoader.js');
                const { DRACOLoader } = await import('three/addons/loaders/DRACOLoader.js');
                const { KTX2Loader } = await import('three/addons/loaders/KTX2Loader.js');
                const { MeshoptDecoder } = await import('three/addons/libs/meshopt_decoder.module.js');

                const dracoLoader = new DRACOLoader();
                dracoLoader.setDecoderPath(`${process.env.REACT_APP_ORIGIN}/libs/draco/gltf/`);

                const ktx2Loader = new KTX2Loader(manager);
                ktx2Loader.setTranscoderPath(`${process.env.REACT_APP_ORIGIN}/libs/basis/`);

                const loader = new GLTFLoader(manager);
                loader.setDRACOLoader(dracoLoader);
                loader.setKTX2Loader(ktx2Loader);
                loader.setMeshoptDecoder(MeshoptDecoder);

                loader.parse(event.target?.result!, '', (gltf) => {
                    loader.dracoLoader?.dispose();
                    loader.ktx2Loader?.dispose();

                    loadedModel = gltf.scene;
                });
            });
            fileReader.readAsArrayBuffer(file);

            break;
        }
        case 'obj': {
            const fileReader = new FileReader();

            fileReader.addEventListener('load', async (event) => {
                const { OBJLoader } = await import('three/addons/loaders/OBJLoader.js');
                const loader = new OBJLoader();

                loadedModel = loader.parse(event.target?.result as string);
            }, false);
            fileReader.readAsText(file);

            break;
        }
        case 'drc': {
            const fileReader = new FileReader();

            fileReader.addEventListener('load', async (event) => {
                const { DRACOLoader } = await import('three/addons/loaders/DRACOLoader.js');

                const loader = new DRACOLoader();
                loader.setDecoderPath(`${process.env.REACT_APP_ORIGIN}/libs/draco/`);

                loader.parse(event.target?.result as ArrayBuffer, (geometry) => {
                    let object;

                    if ( geometry.index !== null ) {

                        const material = new THREE.MeshStandardMaterial();

                        object = new THREE.Mesh(geometry, material);
                        object.name = 'drcModell';

                    } else {

                        const material = new THREE.PointsMaterial({ size: 0.01 });
                        material.vertexColors = geometry.hasAttribute('color');

                        object = new THREE.Points(geometry, material);
                        object.name = 'drcModell';

                    }

                    loadedModel = object;
                });
            }, false);
            fileReader.readAsArrayBuffer(file);
            
            break;
        }
        case 'fbx': {
            const fileReader = new FileReader();

            fileReader.addEventListener('load', async (event) => {
                const manager = new THREE.LoadingManager();
                const { FBXLoader } = await import('three/addons/loaders/FBXLoader.js');

                const loader = new FBXLoader(manager);

                loadedModel = loader.parse(event.target?.result!, '');
            });
            fileReader.readAsArrayBuffer(file);

            break;
        }
        case '3ds': {
            const fileReader = new FileReader();

            fileReader.addEventListener('load', async (event) => {
                const { TDSLoader } = await import('three/addons/loaders/TDSLoader.js');
                const loader = new TDSLoader();

                loadedModel = loader.parse(event.target?.result as ArrayBuffer, '');
            });
            fileReader.readAsArrayBuffer(file);

            break;
        }
    }

    return loadedModel;
};

export const ToolModal = ({ isListModalOpen, setIsListModalOpen }: ToolModalProps) => {
    const [tools, setTools] = useState<Tool[]>([]);
    const dispatch = useDispatch();
    const [prevIsListModalOpen, setPrevIsListModalOpen] = useState(isListModalOpen);
    const [numberOfGetToolsRequest, setNumberOfGetToolsRequest] = useState(0);

    const [isAddModalOpen, setIsAddModalOpen] = useState(false);
    const [addForm] = Form.useForm();
    const [loadedModelForNewTool, setLoadedModelForNewTool] = useState<THREE.Group<THREE.Object3DEventMap> | THREE.Object3D | null>(null);
    
    const [isEditModalOpen, setIsAEditodalOpen] = useState(false);
    const [editForm] = Form.useForm();
    const toolToEditIdRef = useRef<number | null>(null);
    const [loadedModelForEditTool, setLoadedModelForEditTool] = useState<THREE.Group<THREE.Object3DEventMap> | THREE.Object3D | null>(null);
    const [loadedModelForEditToolFromURL, setLoadedModelForEditToolFromURL] = useState<THREE.Group<THREE.Object3DEventMap> | THREE.Object3D | null>(null);
    const [soundEffectForToolToEdit, setSoundEffectForToolToEdit] = useState<string>();
    const [modelFileListForEditForm, setModelFileListForEditForm] = useState<UploadFile[]>();
    const [soundEffectFileListForEditForm, setSoundEffectFileListForEditForm] = useState<UploadFile[]>();

    useEffect(() => {
        let ignore = false;

        const getData = async () => {
            showLoadingAlertMessage();
            const response = await networkCall('/tools');

            dispatch(clearAlertMessage());

            if (ignore) return;

            if (!response || response.status !== 200) {
                dispatch(showAlertMessage({
                    alertMessageType: 'error',
                    alertMessageText: 'Hiba történt a szerszámok lekérdezése közben. Kérjük, próbálja újra!'
                }));
                return;
            }

            const res: AxiosResponse<GetToolsSuccessResponse> = response;
            setTools(res.data.tools);
        };

        if (isListModalOpen) getData();

        return () => {
            ignore = true;
        };
    }, [isListModalOpen, dispatch, numberOfGetToolsRequest]);

    if (isListModalOpen !== prevIsListModalOpen) {
        setPrevIsListModalOpen(isListModalOpen);
        if (!isListModalOpen) {
            setIsAddModalOpen(false);
            setIsAEditodalOpen(false);
        }
    }

    const handleAddFormFinish = async (values: any) => {
        showLoadingAlertMessage();
        
        let model = undefined;
        if (values.model && values.model.length > 0) model = values.model[0].originFileObj;

        let soundEffect = undefined;
        if (values.soundEffect && values.soundEffect.length > 0) soundEffect = values.soundEffect[0].originFileObj;

        const response = await networkCall('/tools', {
            method: 'POST',
            requestBodyType: 'FILE_FORM',
            body: {
                name: values.name,
                usageTime: values.usageTime,
                model,
                soundEffect
            }
        });

        const messageShowed = showDefaultErrorMessages(response);
        if (messageShowed) return;

        dispatch(clearAlertMessage());
        const alertMessageState: AlertMessageState = {
            alertMessageType: 'error',
            alertMessageText: AlertMessages.UnknownErrorTryAgain
        };

        if (response?.status === 201) {

            alertMessageState.alertMessageText = 'Mentés sikeres.';
            alertMessageState.alertMessageType = 'success';
            const res: AxiosResponse<CreateToolSuccessResponse> = response;
            setTools([...tools, res.data.tool]);
            setLoadedModelForNewTool(null);
            setIsAddModalOpen(false);
            addForm.resetFields();

        } else if (response?.status === 400) {
            const res: AxiosResponse<DefaultErrorResponse> = response;

            switch (res.data.code) {

                case 1000:
                    const _res: AxiosResponse<DefaultErrorResponse<ValidationErrorResponseData[]>> = response;
                    alertMessageState.alertMessageText = 'Hibásan megadott beviteli mezők.';
                    const validationErrors = tranformAPIValidationErrorToAntDValidationMessage(_res.data.data!);
                    addForm.setFields(validationErrors);
                    break;

            }

        }

        dispatch(showAlertMessage(alertMessageState));
    };

    const handleAddToolFileUploaderChange = (info: UploadChangeParam) => {
        if (info.fileList && info.fileList.length > 0 && info.fileList[0]?.originFileObj) {
            const model = info.fileList[0].originFileObj;
            const extension = model.name.split('.').pop()?.toLowerCase();
            
            switch (extension) {
                case '3dm': {
                    const fileReader = new FileReader();

                    fileReader.addEventListener('load', async (event) => {
                        const { Rhino3dmLoader } = await import('three/addons/loaders/3DMLoader.js');

                        const loader = new Rhino3dmLoader();
                        loader.setLibraryPath(`${process.env.REACT_APP_ORIGIN}/libs/rhino3dm/`);

                        loader.parse(event.target?.result as ArrayBufferLike , (object) => setLoadedModelForNewTool(object));
                    }, false);
                    fileReader.readAsArrayBuffer(model);

                    break;
                }
                case 'glb':
                case 'gltf': {
                    const fileReader = new FileReader();

                    fileReader.addEventListener('load', async (event) => {
                        const manager = new THREE.LoadingManager();

                        const { GLTFLoader } = await import('three/addons/loaders/GLTFLoader.js');
                        const { DRACOLoader } = await import('three/addons/loaders/DRACOLoader.js');
                        const { KTX2Loader } = await import('three/addons/loaders/KTX2Loader.js');
                        const { MeshoptDecoder } = await import('three/addons/libs/meshopt_decoder.module.js');

                        const dracoLoader = new DRACOLoader();
                        dracoLoader.setDecoderPath(`${process.env.REACT_APP_ORIGIN}/libs/draco/gltf/`);

                        const ktx2Loader = new KTX2Loader(manager);
                        ktx2Loader.setTranscoderPath(`${process.env.REACT_APP_ORIGIN}/libs/basis/`);

                        const loader = new GLTFLoader(manager);
                        loader.setDRACOLoader(dracoLoader);
                        loader.setKTX2Loader(ktx2Loader);
                        loader.setMeshoptDecoder(MeshoptDecoder);

                        loader.parse(event.target?.result!, '', (gltf) => {
                            loader.dracoLoader?.dispose();
                            loader.ktx2Loader?.dispose();

                            setLoadedModelForNewTool(gltf.scene);
                        });
                    });
                    fileReader.readAsArrayBuffer(model);

                    break;
                }
                case 'obj': {
                    const fileReader = new FileReader();

                    fileReader.addEventListener('load', async (event) => {
                        const { OBJLoader } = await import('three/addons/loaders/OBJLoader.js');
                        const loader = new OBJLoader();

                        setLoadedModelForNewTool(loader.parse(event.target?.result as string));
                    }, false);
                    fileReader.readAsText(model);

                    break;
                }
                case 'drc': {
                    const fileReader = new FileReader();

                    fileReader.addEventListener('load', async (event) => {
                        const { DRACOLoader } = await import('three/addons/loaders/DRACOLoader.js');

                        const loader = new DRACOLoader();
                        loader.setDecoderPath(`${process.env.REACT_APP_ORIGIN}/libs/draco/`);

                        loader.parse(event.target?.result as ArrayBuffer, (geometry) => {
                            let object;

                            if ( geometry.index !== null ) {

                                const material = new THREE.MeshStandardMaterial();

                                object = new THREE.Mesh(geometry, material);
                                object.name = 'drcModell';

                            } else {

                                const material = new THREE.PointsMaterial({ size: 0.01 });
                                material.vertexColors = geometry.hasAttribute('color');

                                object = new THREE.Points(geometry, material);
                                object.name = 'drcModell';

                            }

                            setLoadedModelForNewTool(object);
                        });
                    }, false);
                    fileReader.readAsArrayBuffer(model);
                    
                    break;
                }
                case 'fbx': {
                    const fileReader = new FileReader();

                    fileReader.addEventListener('load', async (event) => {
                        const manager = new THREE.LoadingManager();
                        const { FBXLoader } = await import('three/addons/loaders/FBXLoader.js');

                        const loader = new FBXLoader(manager);

                        setLoadedModelForNewTool(loader.parse(event.target?.result!, ''));
                    });
                    fileReader.readAsArrayBuffer(model);

                    break;
                }
                case '3ds': {
                    const fileReader = new FileReader();

                    fileReader.addEventListener('load', async (event) => {
                        const { TDSLoader } = await import('three/addons/loaders/TDSLoader.js');
                        const loader = new TDSLoader();

                        setLoadedModelForNewTool(loader.parse(event.target?.result as ArrayBuffer, ''));
                    });
                    fileReader.readAsArrayBuffer(model);

                    break;
                }
            }

        } else setLoadedModelForNewTool(null);
    };

    const handleDeleteTool = async (id: number) => {
        if(window.confirm('Biztosan törlöd a szerszámot?')) {
            showLoadingAlertMessage();

            const response = await networkCall(`/tools/${ id }`, {
                method: 'DELETE'
            });;

            const messageShowed = showDefaultErrorMessages(response);
            if(messageShowed) return;

            dispatch(clearAlertMessage());

            if(response?.status === 204) {
                setTools(tools.filter(tool => tool.id !== id));
                dispatch(showAlertMessage({
                    alertMessageType: 'success',
                    alertMessageText: 'Törlés sikeres.'
                }));
                return;
            }

            const alertMessageState: AlertMessageState = {
                alertMessageType: 'error',
                alertMessageText: AlertMessages.UnknownErrorTryAgain
            };

            if(response?.status === 404) {
                alertMessageState.alertMessageText = 'Törlés sikertelen, a szerszám nem létezik.';
            }

            dispatch(showAlertMessage(alertMessageState));
        }
    };

    const loadModelFromURL = (url: string) => {
        const extension = url.split('.').pop()?.toLowerCase();
        const model = url;

        switch (extension) {
            case '3dm': {
                const loadModel = async () => {
                    const { Rhino3dmLoader } = await import('three/addons/loaders/3DMLoader.js');
                    const loader = new Rhino3dmLoader();
                    loader.setLibraryPath(`${process.env.REACT_APP_ORIGIN}/libs/rhino3dm/`);

                    loader.load(model, (object) => setLoadedModelForEditToolFromURL(object));
                };
                loadModel();
                break;
            }
            case 'glb':
            case 'gltf': {
                const loadModel = async () => {
                    const manager = new THREE.LoadingManager();

                    const { GLTFLoader } = await import('three/addons/loaders/GLTFLoader.js');
                    const { DRACOLoader } = await import('three/addons/loaders/DRACOLoader.js');
                    const { KTX2Loader } = await import('three/addons/loaders/KTX2Loader.js');
                    const { MeshoptDecoder } = await import('three/addons/libs/meshopt_decoder.module.js');

                    const dracoLoader = new DRACOLoader();
                    dracoLoader.setDecoderPath(`${process.env.REACT_APP_ORIGIN}/libs/draco/gltf/`);

                    const ktx2Loader = new KTX2Loader(manager);
                    ktx2Loader.setTranscoderPath(`${process.env.REACT_APP_ORIGIN}/libs/basis/`);

                    const loader = new GLTFLoader(manager);
                    loader.setDRACOLoader(dracoLoader);
                    loader.setKTX2Loader(ktx2Loader);
                    loader.setMeshoptDecoder(MeshoptDecoder);

                    loader.load(model, (gltf) => setLoadedModelForEditToolFromURL(gltf.scene));
                };
                loadModel();

                break;
            }
            case 'obj': {
                const loadModel = async () => {
                    const { OBJLoader } = await import('three/addons/loaders/OBJLoader.js');

                    const loader = new OBJLoader();
                    
                    loader.load(model, (object) => {
                        setLoadedModelForEditToolFromURL(object);
                    });
                };
                loadModel();
                break;
            }
            case 'drc': {
                const loadModel = async () => {
                    const { DRACOLoader } = await import('three/addons/loaders/DRACOLoader.js');

                    const loader = new DRACOLoader();
                    loader.setDecoderPath(`${process.env.REACT_APP_ORIGIN}/libs/draco/`);

                    loader.load(model, (geometry) => {
                        let object;

                        if ( geometry.index !== null ) {

                            const material = new THREE.MeshStandardMaterial();

                            object = new THREE.Mesh(geometry, material);
                            object.name = 'drcModell';

                        } else {

                            const material = new THREE.PointsMaterial({ size: 0.01 });
                            material.vertexColors = geometry.hasAttribute('color');

                            object = new THREE.Points(geometry, material);
                            object.name = 'drcModell';

                        }

                        setLoadedModelForEditToolFromURL(object);
                    });
                };
                loadModel();
                
                break;
            }
            case 'fbx': {
                const loadModel = async () => {
                    const manager = new THREE.LoadingManager();
                    const { FBXLoader } = await import('three/addons/loaders/FBXLoader.js');

                    const loader = new FBXLoader(manager);

                    loader.load(model, (object) => setLoadedModelForEditToolFromURL(object));
                };
                loadModel();

                break;
            }
            //case 'max':
            case '3ds': {
                const loadModel = async () => {
                    const { TDSLoader } = await import('three/addons/loaders/TDSLoader.js');
                    const loader = new TDSLoader();

                    loader.load(model, (object) => setLoadedModelForEditToolFromURL(object));
                };
                loadModel();

                break;
            }
        }
    };

    const handleEditTool = (id: number) => {
        const tool = tools.find(tool => tool.id === id);

        if (!tool) {
            alert('A szerszám nem létezik.');
            return;
        }

        setIsAddModalOpen(false);
        setIsAEditodalOpen(true);
        toolToEditIdRef.current = id;

        editForm.setFields([
            {
                name: 'name',
                value: tool.name
            },
            {
                name: 'usageTime',
                value: tool.usageTime
            }
        ]);

        setSoundEffectForToolToEdit(tool.soundEffect);

        if (!tool.model) return;

        loadModelFromURL(tool.model);
    };

    const handleEditToolFormModelFileUploaderChange = (info: UploadChangeParam) => {
        setModelFileListForEditForm(info.fileList);

        if (info.fileList && info.fileList.length > 0 && info.fileList[0]?.originFileObj) {
            const model = info.fileList[0].originFileObj;
            const extension = model.name.split('.').pop()?.toLowerCase();
            
            switch (extension) {
                case '3dm': {
                    const fileReader = new FileReader();

                    fileReader.addEventListener('load', async (event) => {
                        const { Rhino3dmLoader } = await import('three/addons/loaders/3DMLoader.js');

                        const loader = new Rhino3dmLoader();
                        loader.setLibraryPath(`${process.env.REACT_APP_ORIGIN}/libs/rhino3dm/`);

                        loader.parse(event.target?.result as ArrayBufferLike , (object) => setLoadedModelForEditTool(object));
                    }, false);
                    fileReader.readAsArrayBuffer(model);

                    break;
                }
                case 'glb':
                case 'gltf': {
                    const fileReader = new FileReader();

                    fileReader.addEventListener('load', async (event) => {
                        const manager = new THREE.LoadingManager();

                        const { GLTFLoader } = await import('three/addons/loaders/GLTFLoader.js');
                        const { DRACOLoader } = await import('three/addons/loaders/DRACOLoader.js');
                        const { KTX2Loader } = await import('three/addons/loaders/KTX2Loader.js');
                        const { MeshoptDecoder } = await import('three/addons/libs/meshopt_decoder.module.js');

                        const dracoLoader = new DRACOLoader();
                        dracoLoader.setDecoderPath(`${process.env.REACT_APP_ORIGIN}/libs/draco/gltf/`);

                        const ktx2Loader = new KTX2Loader(manager);
                        ktx2Loader.setTranscoderPath(`${process.env.REACT_APP_ORIGIN}/libs/basis/`);

                        const loader = new GLTFLoader(manager);
                        loader.setDRACOLoader(dracoLoader);
                        loader.setKTX2Loader(ktx2Loader);
                        loader.setMeshoptDecoder(MeshoptDecoder);

                        loader.parse(event.target?.result!, '', (gltf) => {
                            loader.dracoLoader?.dispose();
                            loader.ktx2Loader?.dispose();

                            setLoadedModelForEditTool(gltf.scene);
                        });
                    });
                    fileReader.readAsArrayBuffer(model);

                    break;
                }
                case 'obj': {
                    const fileReader = new FileReader();

                    fileReader.addEventListener('load', async (event) => {
                        const { OBJLoader } = await import('three/addons/loaders/OBJLoader.js');
                        const loader = new OBJLoader();

                        setLoadedModelForEditTool(loader.parse(event.target?.result as string));
                    }, false);
                    fileReader.readAsText(model);

                    break;
                }
                case 'drc': {
                    const fileReader = new FileReader();

                    fileReader.addEventListener('load', async (event) => {
                        const { DRACOLoader } = await import('three/addons/loaders/DRACOLoader.js');

                        const loader = new DRACOLoader();
                        loader.setDecoderPath(`${process.env.REACT_APP_ORIGIN}/libs/draco/`);

                        loader.parse(event.target?.result as ArrayBuffer, (geometry) => {
                            let object;

                            if ( geometry.index !== null ) {

                                const material = new THREE.MeshStandardMaterial();

                                object = new THREE.Mesh(geometry, material);
                                object.name = 'drcModell';

                            } else {

                                const material = new THREE.PointsMaterial({ size: 0.01 });
                                material.vertexColors = geometry.hasAttribute('color');

                                object = new THREE.Points(geometry, material);
                                object.name = 'drcModell';

                            }

                            setLoadedModelForEditTool(object);
                        });
                    }, false);
                    fileReader.readAsArrayBuffer(model);
                    
                    break;
                }
                case 'fbx': {
                    const fileReader = new FileReader();

                    fileReader.addEventListener('load', async (event) => {
                        const manager = new THREE.LoadingManager();
                        const { FBXLoader } = await import('three/addons/loaders/FBXLoader.js');

                        const loader = new FBXLoader(manager);

                        setLoadedModelForEditTool(loader.parse(event.target?.result!, ''));
                    });
                    fileReader.readAsArrayBuffer(model);

                    break;
                }
                case '3ds': {
                    const fileReader = new FileReader();

                    fileReader.addEventListener('load', async (event) => {
                        const { TDSLoader } = await import('three/addons/loaders/TDSLoader.js');
                        const loader = new TDSLoader();

                        setLoadedModelForEditTool(loader.parse(event.target?.result as ArrayBuffer, ''));
                    });
                    fileReader.readAsArrayBuffer(model);

                    break;
                }
            }

        } else setLoadedModelForEditTool(null);
    };

    const handleEditToolFormSoundEffectFileUploaderChange = (info: UploadChangeParam) => {
        setSoundEffectFileListForEditForm(info.fileList);
    };

    const handleEditModalClosed = () => {
        editForm.resetFields();
        setSoundEffectForToolToEdit(undefined);
        toolToEditIdRef.current = null;
        setLoadedModelForEditTool(null);
        setLoadedModelForEditToolFromURL(null);
        setModelFileListForEditForm(undefined);
        setSoundEffectFileListForEditForm(undefined);
    };

    const handleEditFormFinish = async (values: any) => {
        showLoadingAlertMessage();
        
        let model = undefined;
        if (values.model && values.model.length > 0) model = values.model[0].originFileObj;

        let soundEffect = undefined;
        if (values.soundEffect && values.soundEffect.length > 0) soundEffect = values.soundEffect[0].originFileObj;

        const response = await networkCall(`/tools/${ toolToEditIdRef.current }`, {
            method: 'PUT',
            requestBodyType: 'FILE_FORM',
            body: {
                name: values.name,
                usageTime: values.usageTime,
                model,
                soundEffect
            }
        });

        const messageShowed = showDefaultErrorMessages(response);
        if (messageShowed) return;

        dispatch(clearAlertMessage());
        const alertMessageState: AlertMessageState = {
            alertMessageType: 'error',
            alertMessageText: AlertMessages.UnknownErrorTryAgain
        };

        if (response?.status === 200) {

            alertMessageState.alertMessageText = 'Mentés sikeres.';
            alertMessageState.alertMessageType = 'success';
            
            const res: AxiosResponse<CreateToolSuccessResponse> = response;
            setLoadedModelForEditTool(null);
            setModelFileListForEditForm(undefined);
            setSoundEffectFileListForEditForm(undefined);
            setLoadedModelForEditTool(null);
            if (res.data.tool.model) loadModelFromURL(res.data.tool.model);
            if (res.data.tool.soundEffect) setSoundEffectForToolToEdit(res.data.tool.soundEffect);
            
            setTools(tools.map(tool => {
                if (tool.id === res.data.tool.id) return res.data.tool;
                else return tool;
            }));

        } else if (response?.status === 400) {
            const res: AxiosResponse<DefaultErrorResponse> = response;

            switch (res.data.code) {

                case 1000:
                    const _res: AxiosResponse<DefaultErrorResponse<ValidationErrorResponseData[]>> = response;
                    alertMessageState.alertMessageText = 'Hibásan megadott beviteli mezők.';
                    const validationErrors = tranformAPIValidationErrorToAntDValidationMessage(_res.data.data!);
                    addForm.setFields(validationErrors);
                    break;

            }

        } else if (response?.status === 404) {
            const res: AxiosResponse<DefaultErrorResponse> = response;

            switch (res.data.code) {
                case 17000:
                    alertMessageState.alertMessageText = 'A szerszám nem található.';
                    break;
            }
        }

        dispatch(showAlertMessage(alertMessageState));
    };

    return (
        <>
            {/* List modal */}
            <Modal className="list-tool-modal" isOpen={isListModalOpen} toggle={() => setIsListModalOpen(!isListModalOpen)} size='lg'>
                <ModalHeader className="d-block">
                    <div className="d-flex justify-content-between align-items-center blue-font">
                        <b>Szerszámok kezelése</b>
                        <img
                            style={{ width: '50px', padding: '10px 10px 0 0' }}
                            src={getImageUrl('close-button.png')}
                            alt="close-button"
                            title="Bezárás"
                            className="cursor-pointer"
                            onClick={() => setIsListModalOpen(false)}
                        />
                    </div>
                </ModalHeader>
                <ModalBody>
                    <Container>
                        <Row>
                            {
                                tools.map((tool) => (
                                    <Col xs={6} sm={4} key={tool.id}>
                                        <ToolCard
                                            tool={tool}
                                            type="edit"
                                            onDelete={() => handleDeleteTool(tool.id)}
                                            onEdit={() => handleEditTool(tool.id)}
                                        />
                                    </Col>
                                ))
                            }
                            <Col
                                xs={{ size: 6, offset: tools.length % 2 === 0 ? 3 : 0 }}
                                sm={{ size: 4, offset: tools.length % 3 === 0 ? 4 : 0 }}
                                className="add-card box-shadow-4-4-8-0"
                                onClick={() => setIsAddModalOpen(true)}
                            >
                                <div className='text-center'>Új hozzáadása</div>
                            </Col>
                        </Row>
                    </Container>
                </ModalBody>
            </Modal>

            {/* Add modal */}
            <Modal
                className="add-tool-modal"
                isOpen={isAddModalOpen}
                toggle={
                    () => {
                        setIsAddModalOpen(!isAddModalOpen);
                        if (!isAddModalOpen) setIsAEditodalOpen(false);
                    }
                }
            >
                <ModalHeader className="d-block">
                    <div className="d-flex justify-content-between align-items-center blue-font">
                        <b>Új szerszám hozzáadása</b>
                        <img
                            style={{ width: '50px', padding: '10px 10px 0 0' }}
                            src={getImageUrl('close-button.png')}
                            alt="close-button"
                            title="Bezárás"
                            className="cursor-pointer"
                            onClick={() => setIsAddModalOpen(false)}
                        />
                    </div>
                </ModalHeader>
                <ModalBody>
                    <Form
                        form={addForm}
                        onFinish={handleAddFormFinish}
                        layout='vertical'
                    >

                        <Form.Item
                            label={<b>Név</b>}
                            name='name'
                            rules={[{required: true, message: 'Név megadása kötelező!'}]}
                            className='mb-2'
                        >
                            <Input className='box-shadow-4-4-8-0 border-0' placeholder="Név megadása" />
                        </Form.Item>

                        <Form.Item
                            label={<b>Használati idő</b>}
                            name='usageTime'
                            className='mb-4'
                        >
                            <InputNumber
                                className='box-shadow-4-4-8-0 border-0'
                                placeholder="10"
                                suffix={'mp'}
                                style={{ width: '100%' }}
                                onKeyDown={event => {
                                    if (!['Backspace', 'Delete', 'ArrowLeft', 'ArrowRight','0', '1', '2', '3', '4', '5', '6', '7', '8', '9', '.', ','].includes(event.key))
                                        event.preventDefault();
                                }}
                                step={1}
                                min={0}
                            />
                        </Form.Item>

                        <Form.Item
                            name="model"
                            label={<b>Modell</b>}
                            rules={[{required: true, message: 'Modell feltöltése kötelező!'}]}
                            getValueFromEvent={normFile}
                        >
                            <Upload
                                maxCount={1}
                                multiple={false}
                                beforeUpload={() => false}
                                accept='.3dm, .glb, .gltf, .obj, .drc, .fbx, .3ds'
                                onChange={handleAddToolFileUploaderChange}
                            >
                                <Button icon={<UploadOutlined />}>Feltöltés</Button>
                            </Upload>
                        </Form.Item>

                        {
                            loadedModelForNewTool !== null && (
                                <Canvas camera={{ position: [2, 2, 8] }}>
                                    <primitive object={loadedModelForNewTool} />
                                    <OrbitControls />
                                    <ambientLight />
                                </Canvas>
                            )
                        }

                        <Form.Item
                            name="soundEffect"
                            label={<b>Hangeffekt</b>}
                            getValueFromEvent={normFile}
                        >
                            <Upload
                                maxCount={1}
                                multiple={false}
                                beforeUpload={() => false}
                                accept='audio/*'
                            >
                                <Button icon={<UploadOutlined />}>Feltöltés</Button>
                            </Upload>
                        </Form.Item>
                        
                        <Form.Item className="text-center">
                            <CMSButton text='Mentés' iconSrc={getImageUrl('Save-icon.png')} htmlType="submit" />
                        </Form.Item>

                    </Form>
                </ModalBody>
            </Modal>

            {/* Edit modal */}
            <Modal
                className="edit-tool-modal"
                isOpen={isEditModalOpen}
                toggle={
                    () => {
                        setIsAEditodalOpen(!isEditModalOpen);
                        if (!isEditModalOpen) setIsAddModalOpen(false);
                    }
                }
                onClosed={handleEditModalClosed}
            >
                <ModalHeader className="d-block">
                    <div className="d-flex justify-content-between align-items-center blue-font">
                        <b>Szerszám módosítása</b>
                        <img
                            style={{ width: '50px', padding: '10px 10px 0 0' }}
                            src={getImageUrl('close-button.png')}
                            alt="close-button"
                            title="Bezárás"
                            className="cursor-pointer"
                            onClick={() => setIsAEditodalOpen(false)}
                        />
                    </div>
                </ModalHeader>
                <ModalBody>
                    <Form
                        form={editForm}
                        onFinish={handleEditFormFinish}
                        layout='vertical'
                    >

                        <Form.Item
                            label={<b>Név</b>}
                            name='name'
                            rules={[{required: true, message: 'Név megadása kötelező!'}]}
                            className='mb-2'
                        >
                            <Input className='box-shadow-4-4-8-0 border-0' placeholder="Név megadása" />
                        </Form.Item>

                        <Form.Item
                            label={<b>Használati idő</b>}
                            name='usageTime'
                            className='mb-4'
                        >
                            <InputNumber
                                className='box-shadow-4-4-8-0 border-0'
                                placeholder="10"
                                suffix={'mp'}
                                style={{ width: '100%' }}
                                onKeyDown={event => {
                                    if (!['Backspace', 'Delete', 'ArrowLeft', 'ArrowRight','0', '1', '2', '3', '4', '5', '6', '7', '8', '9', '.', ','].includes(event.key))
                                        event.preventDefault();
                                }}
                                step={1}
                                min={0}
                            />
                        </Form.Item>

                        <Form.Item
                            name="model"
                            label={<b>Modell</b>}
                            getValueFromEvent={normFile}
                            className='mb-2'
                        >
                            <Upload
                                fileList={modelFileListForEditForm}
                                maxCount={1}
                                multiple={false}
                                beforeUpload={() => false}
                                accept='.3dm, .glb, .gltf, .obj, .drc, .fbx, .3ds'
                                onChange={handleEditToolFormModelFileUploaderChange}
                            >
                                <Button icon={<UploadOutlined />}>Feltöltés</Button>
                            </Upload>
                        </Form.Item>

                        {
                            loadedModelForEditTool !== null && (
                                <>
                                    <p>Új modell:</p>
                                    <Canvas camera={{ position: [2, 2, 8] }}>
                                        <primitive object={loadedModelForEditTool} />
                                        <OrbitControls />
                                        <ambientLight />
                                    </Canvas>
                                </>
                            )
                        }

                        {
                            loadedModelForEditToolFromURL !== null && (
                                <>
                                    <p>Jelenlegi modell:</p>
                                    <Canvas camera={{ position: [2, 2, 8] }}>
                                        <primitive object={loadedModelForEditToolFromURL} />
                                        <OrbitControls />
                                        <ambientLight />
                                    </Canvas>
                                </>
                            )
                        }

                        <Form.Item
                            name="soundEffect"
                            label={<b>Hangeffekt</b>}
                            getValueFromEvent={normFile}
                            className='mb-2'
                        >
                            <Upload
                                maxCount={1}
                                multiple={false}
                                beforeUpload={() => false}
                                accept='audio/*'
                                fileList={soundEffectFileListForEditForm}
                                onChange={handleEditToolFormSoundEffectFileUploaderChange}
                            >
                                <Button icon={<UploadOutlined />}>Feltöltés</Button>
                            </Upload>
                        </Form.Item>

                        {
                            soundEffectForToolToEdit && (
                                <p>Jelenlegi hangeffekt: <a href={soundEffectForToolToEdit} target="_blank">{ soundEffectForToolToEdit.split('/').pop() ?? soundEffectForToolToEdit.split('\\').pop() }</a></p>
                            )
                        }
                        
                        <Form.Item className="text-end">
                            <CMSButton text='Mentés' iconSrc={getImageUrl('Save-icon.png')} htmlType="submit" />
                        </Form.Item>

                    </Form>
                </ModalBody>
            </Modal>
    
        </>
    );
};